import { Download } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import axios from '@/portainer/services/axios/axios';
import { notifyError } from '@/portainer/services/notifications';

import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { FormSection } from '@@/form-components/FormSection';
import { BoxSelector } from '@@/BoxSelector';

import { BackupFormType, options } from './backup-options';
import { BackupFileForm } from './BackupFileForm';
import { BackupLocalForm } from './BackupLocalForm';
import { BackupS3Form } from './BackupS3Form';
import { BackupAzureForm } from './BackupAzureForm';
import { useBackupS3Settings } from './queries/useBackupS3Settings';
import { useBackupAzureSettings } from './queries/useBackupAzureSettings';
import { useBackupLocalSettings } from './queries/useBackupLocalSettings';
import { buildUrl } from './queries/backupSettings.service';
import { queryKeys } from './queries/queryKeys';

export function BackupSettingsPanel() {
  const [backupType, setBackupType] = useState(options[0].value);
  const initialTypeSet = useRef(false);
  const queryClient = useQueryClient();

  const s3SettingsQuery = useBackupS3Settings({ enabled: isBE });
  const azureSettingsQuery = useBackupAzureSettings({ enabled: isBE });
  const localSettingsQuery = useBackupLocalSettings({ enabled: isBE });

  useEffect(() => {
    // Run only once — after the initial load all queries have settled.
    // Subsequent refetches (e.g. after saving settings) must not override
    // the tab the user is currently on.
    if (initialTypeSet.current) return;
    if (
      !azureSettingsQuery.isFetched ||
      !s3SettingsQuery.isFetched ||
      !localSettingsQuery.isFetched
    )
      return;

    initialTypeSet.current = true;

    // Azure takes precedence if both are configured — check it first
    if (azureSettingsQuery.data?.storageAccountName) {
      setBackupType(BackupFormType.Azure);
    } else if (s3SettingsQuery.data?.bucketName) {
      // Use bucketName as the indicator for S3 mode, since credentials can be empty
      // when using IRSA or other default credential provider chains
      setBackupType(BackupFormType.S3);
    } else if (localSettingsQuery.data?.cronRule) {
      setBackupType(BackupFormType.Local);
    }
  }, [
    azureSettingsQuery.isFetched,
    azureSettingsQuery.data,
    s3SettingsQuery.isFetched,
    s3SettingsQuery.data,
    localSettingsQuery.isFetched,
    localSettingsQuery.data,
  ]);

  return (
    <Widget id="backup-settings">
      <WidgetTitle icon={Download} title="Back up Portainer" />
      <WidgetBody>
        <div className="form-horizontal">
          <FormSection title="Backup configuration">
            <div className="form-group col-sm-12 text-muted small">
              This will back up your Portainer server configuration and does not
              include containers.
            </div>
            <BoxSelector
              slim
              options={options}
              value={backupType}
              onChange={(v) => setBackupType(v)}
              radioName="backup-type"
            />

            {backupType === BackupFormType.S3 && (
              <BackupS3Form onSave={() => handleSave(BackupFormType.S3)} />
            )}
            {backupType === BackupFormType.Azure && (
              <BackupAzureForm
                onSave={() => handleSave(BackupFormType.Azure)}
              />
            )}
            {backupType === BackupFormType.Local && (
              <BackupLocalForm
                onSave={() => handleSave(BackupFormType.Local)}
              />
            )}
            {backupType === BackupFormType.File && <BackupFileForm />}
          </FormSection>
        </div>
      </WidgetBody>
    </Widget>
  );

  function handleSave(savedType: BackupFormType) {
    if (
      savedType !== BackupFormType.Azure &&
      azureSettingsQuery.data?.storageAccountName
    ) {
      void resetAzureSettings();
    }

    if (savedType !== BackupFormType.S3 && s3SettingsQuery.data?.bucketName) {
      void resetS3Settings();
    }

    if (
      savedType !== BackupFormType.Local &&
      localSettingsQuery.data?.cronRule
    ) {
      void resetLocalSettings();
    }
  }

  async function resetAzureSettings() {
    try {
      await axios.post(buildUrl('azure', 'settings'), {
        cronRule: '',
        storageAccountName: '',
        containerName: '',
        authMethod: 'servicePrincipalSecret',
        tenantId: '',
        clientId: '',
        clientSecret: '',
        clientCertificate: '',
        clientCertificatePassword: '',
        managedIdentityClientId: '',
        storageAccountKey: '',
        serviceUrl: '',
        password: '',
      });
      queryClient.invalidateQueries(queryKeys.backupAzureSettings());
    } catch (e) {
      notifyError('Failed to reset Azure backup settings', e as Error);
    }
  }

  async function resetS3Settings() {
    try {
      await axios.post(buildUrl('s3', 'settings'), {
        cronRule: '',
        accessKeyID: '',
        secretAccessKey: '',
        region: '',
        bucketName: '',
        s3CompatibleHost: '',
        password: '',
      });
      queryClient.invalidateQueries(queryKeys.backupS3Settings());
    } catch (e) {
      notifyError('Failed to reset S3 backup settings', e as Error);
    }
  }

  async function resetLocalSettings() {
    try {
      await axios.post(buildUrl('local', 'settings'), {
        cronRule: '',
        retentionDays: 0,
      });
      queryClient.invalidateQueries(queryKeys.backupLocalSettings());
    } catch (e) {
      notifyError('Failed to reset local backup settings', e as Error);
    }
  }
}
